import React from 'react';
import OperationStatusList from './OperationStatusList';
import AIReviewMessageActions from './AIReviewMessageActions';
import { typography, surfaces, borders, textColors } from '../../../design-system/tokens';

const AIMessageBubble = ({ message, chatId, onPrefillDraft, onFocusComposer, isLatestMessage = true }) => {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const operations = message.operations || [];
  const hasOperations = isAssistant && operations.length > 0;
  const messageType = message?.context?.message_type;
  const reviewModeEvent = message?.context?.review_mode_event;
  const isReviewAssistantMessage = isAssistant && (
    messageType === 'review_consultation' ||
    messageType === 'review_approval' ||
    messageType === 'review_applied' ||
    messageType === 'review_post_apply'
  );
  const shouldShowReviewActions = isAssistant && (
    messageType === 'review_consultation' ||
    messageType === 'review_approval' ||
    messageType === 'review_post_apply'
  );
  const assistantBubbleClassName = isReviewAssistantMessage
    ? 'bg-green-400/10 dark:bg-green-400/10 border border-green-400/25 backdrop-blur-sm'
    : `${surfaces.primary} border ${borders.default} ${textColors.primary} dark:text-white`;
  
  // Check completion status
  const allCompleted = hasOperations && operations.every(op => 
    op.execution_status === 'completed' || op.execution_status === 'failed'
  );
  const anyFailed = hasOperations && operations.some(op => op.execution_status === 'failed');
  
  // Extract clarification type from message context (for assistant messages)
  const clarificationType = message.context?.clarification_type;
  const elementCount = message.context?.element_count;
  const elementType = message.context?.element_type;

  if (messageType === 'review_mode_marker') {
    const markerTextClass =
      reviewModeEvent === 'started'
        ? 'text-green-600 dark:text-green-400'
        : `${textColors.secondary}`;

    return (
      <div className="flex items-center gap-3 py-1">
        <div className={`h-px flex-1 border-t ${borders.default}`} />
        <span className={`${typography.caption} ${markerTextClass} uppercase tracking-[0.18em]`}>
          {message.content}
        </span>
        <div className={`h-px flex-1 border-t ${borders.default}`} />
      </div>
    );
  }
  
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[80%] ${isUser ? 'order-2' : 'order-1'}`}>
        {/* Message bubble */}
        <div
          data-testid="ai-message-bubble-surface"
          data-review-surface={isReviewAssistantMessage ? 'true' : 'false'}
          className={`rounded-lg p-3 ${
            isUser 
              ? 'bg-blue-600 dark:bg-blue-700 text-white' 
              : assistantBubbleClassName
          }`}
        >
          <p
            data-review-message={isReviewAssistantMessage ? 'true' : 'false'}
            className={`${isReviewAssistantMessage ? 'text-[13px] leading-6' : 'text-sm'} whitespace-pre-wrap break-words ${
              isUser ? 'text-white' : `${textColors.primary} dark:text-white`
            }`}
          >
            {message.content}
          </p>
          
          {/* Clarification type indicators (for assistant messages) */}
          {isAssistant && clarificationType && (
            <div className="mt-2">
              {clarificationType === 'not_found' && (
                <div className="text-orange-600 dark:text-orange-400 text-xs flex items-center gap-1">
                  <span>🔍</span>
                  <span>Searching broader scope...</span>
                </div>
              )}
              
              {clarificationType === 'multiple_matches' && (
                <div className="text-blue-600 dark:text-blue-400 text-xs flex items-center gap-1">
                  <span>❓</span>
                  <span>
                    Multiple matches found
                    {elementCount && elementType && ` (${elementCount} ${elementType}${elementCount > 1 ? 's' : ''})`}
                    {' - '}please clarify
                  </span>
                </div>
              )}
              
              {clarificationType === 'unclear' && (
                <div className="text-blue-600 dark:text-blue-400 text-xs flex items-center gap-1">
                  <span>❓</span>
                  <span>Request unclear - please provide more details</span>
                </div>
              )}
              
              {clarificationType === 'missing_context' && (
                <div className="text-amber-600 dark:text-amber-400 text-xs flex items-center gap-1">
                  <span>⚠️</span>
                  <span>Missing information - please provide more context</span>
                </div>
              )}
            </div>
          )}
          
          <span className={`text-xs mt-2 block ${isUser ? 'text-white' : `${textColors.primary} dark:text-white`}`}>
            {new Date(message.timestamp || Date.now()).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}
          </span>
          
          {/* Operations status list (inline with message) */}
          {hasOperations && (
            <>
              <OperationStatusList operations={operations} />
              
              {/* Completion status message */}
              {allCompleted && !anyFailed && (
                <p className={`${typography.caption} text-green-600 dark:text-green-400 mt-2`}>
                  ✓ All changes applied successfully
                </p>
              )}
              
              {anyFailed && (
                <p className={`${typography.caption} text-orange-600 dark:text-orange-400 mt-2`}>
                  ⚠ Some changes failed. See details above.
                </p>
              )}
            </>
          )}

          {shouldShowReviewActions && (
            <AIReviewMessageActions
              chatId={chatId}
              message={message}
              isLatestMessage={isLatestMessage}
              onPrefillDraft={onPrefillDraft}
              onFocusComposer={onFocusComposer}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AIMessageBubble;
